<template>
  <mailbox-settings-layout :name="officeHoursInfo.name">
    <el-card class="mailbox-settings-office-hours">
      <template #header>
        <h3>Office Hours</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Enter your weekly office schedule. You can configure email
          <router-link :to="RouteAssistant.getMailboxSettingsAutoResponderRoute($route.params.accountId, id)" class="just-a-link">auto-responses</router-link>
          to send to your customers during or after these hours.
        </p>
        <div v-if="!officeHoursInfo.hasSet" class="mailbox-settings-office-hours__form-toggle">
          <el-switch v-model="expandForm" />Set office hours
        </div>
        <template v-if="expandForm">
          <el-form
            ref="timeZoneForm"
            :model="officeHoursInfo"
            :rules="formRules"
            label-position="top"
            class="mailbox-settings-office-hours__form"
          >
            <!-- Timezone -->
            <el-form-item
              label="Timezone"
              class="mailbox-settings-office-hours__form__timezone"
            >
              <single-select v-model="officeHoursInfo.timeZoneID" :disabled="isSubmitting" placeholder="Select a timezone...">
                <el-option v-for="tz in officeHoursInfo.listTimeZones" :key="tz.id" :value="tz.id" :label="tz.name" />
              </single-select>
            </el-form-item>
            <!-- Weekly area -->
            <mailbox-settings-office-hours-day
              v-for="(day, index) in officeHoursInfo.listDays"
              :key="index"
              :index="index"
              :day="day"
              :disabled="isSubmitting"
            />
          </el-form>
          <dd-button
            :is-loading="isSubmitting"
            :color="ColorAssistant.MailboxColor"
            class="float-right"
            size="large"
            @click="updateOfficeHours()"
          >Save Changes</dd-button>
          <div class="clearfix" />
        </template>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsOfficeHoursDay from './components/mailbox-settings-office-hours-day.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import {
  IMailboxesOutputOfficeHours,
  IOfficeHourDay,
} from '@/api/types/response.types'
import { IMailboxesInputOfficeHours } from '@/api/types/request.types'

export default defineComponent({
  name: 'MailboxSettingsAutoResponder',

  components: {
    MailboxSettingsLayout,
    MailboxSettingsOfficeHoursDay,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      isLoading: true,
      isSubmitting: false,
      expandForm: false,
      officeHoursInfo: {
        name: '',
      } as IMailboxesOutputOfficeHours,
      pickerFormat: 'hh:mm A',
    }
  },

  head(): any {
    return {
      title: `Office Hours - ${this.officeHoursInfo.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    formRules(): any {
      const rules = {} as any

      this.officeHoursInfo.listDays.forEach(
        (element: IOfficeHourDay, idx: number) => {
          rules[`listDays[${idx}].openTime`] = [
            { validator: this.validateHours, trigger: 'submit' },
          ]
        },
      )
      return rules
    },
  },

  async created() {
    this.isLoading = true
    await this.getOfficeHoursInfo()
    this.isLoading = false
  },

  methods: {
    async getOfficeHoursInfo() {
      try {
        this.officeHoursInfo = await MailboxesAPI.get_OfficeHours(this.id)
        this.expandForm = this.officeHoursInfo.hasSet
      } catch (error) {
        this.$error.emit(error)
      }
    },

    validateHours(rule: any, value: any, callback: any) {
      const fieldIndex = parseInt(
        rule.field.split(/\[[^\d]*(\d+)[^\d]*\]/)[1],
        0,
      )
      if (this.officeHoursInfo.listDays[fieldIndex].isOpen) {
        const openTime = dayjs(
          this.officeHoursInfo.listDays[fieldIndex].openTime,
          'hh:mm A',
        )
        const closeTime = dayjs(
          this.officeHoursInfo.listDays[fieldIndex].closeTime,
          'hh:mm A',
        )
        const valid = openTime.isBefore(closeTime)
        if (!valid) {
          const message = 'Open time must be before close time'
          return callback(new Error(message))
        }
      }

      return callback()
    },

    async updateOfficeHours() {
      // @ts-ignore
      await this.$refs.timeZoneForm.validate(async (valid: boolean) => {
        if (valid) {
          try {
            this.isSubmitting = true
            const formData = {
              timeZoneID: this.officeHoursInfo.timeZoneID,
              listDays: this.officeHoursInfo.listDays,
            } as IMailboxesInputOfficeHours
            const response = await MailboxesAPI.post_OfficeHours(
              this.id,
              formData,
            )
            this.$messaging.showSuccess(response.message)
          } catch (error) {
            this.$error.emit(error, true)
          } finally {
            this.isSubmitting = false
          }
        } else {
          return false
        }
      })
    },
  },
})
</script>

<style lang="scss" scoped>
.mailbox-settings-office-hours {
  &__form-toggle {
    margin-top: 24px;
    margin-bottom: 24px;
    @include lettering--p;

    .el-switch {
      margin-right: 7px;
    }
  }
  &__form {
    :deep(.el-input .el-input__inner:-ms-input-placeholder) {
      padding: 8px;
      line-height: 1.44;
      color: $slate !important;

      :focus {
        margin-left: 1px;
      }
    }

    &__timezone {
      margin-top: 20px;
    }
  }
}
</style>
